import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { AppButton } from '../../../../Components/FormKit';
import { labelPlaca } from '../../../../Utils/statePlateAddress';
import { budgetDetailModalCSS, budgetDetailDialogStyle, resumenChipCSS, sourceChipCSS } from './BudgetsManagement.css';

const PLANES = { financiero: 'Financiero', puro: 'Puro', cotizador3: 'Calculadora 3' };
const TIPOS_VEHICULO = { 1: 'Nuevo', 2: 'Seminuevo' };
const MOTORES = { 1: 'Gasolina/Diesel', 2: 'Híbrido/Eléctrico' };

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 650);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 650);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
};

const initialsOf = (name) => {
    if (!name) return '·';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '·';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const formatCurrency = (value) =>
    value != null ? `$${Number(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

const formatPct = (value) => `${Number(value).toLocaleString('es-MX', { maximumFractionDigits: 2 })}%`;

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const now = new Date();
        
        // Reset hours/minutes/seconds for date-only comparison
        const dDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const diffTime = nowDate.getTime() - dDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
            // Future date fallback
            return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        
        if (diffDays === 0) {
            return 'hoy';
        }
        if (diffDays === 1) {
            return 'ayer';
        }
        if (diffDays < 7) {
            return `hace ${diffDays} días`;
        }
        
        const day = String(d.getDate()).padStart(2, '0');
        const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return '—';
    }
};

const Field = ({ label, value, currency, muted }) => (
    <div className="bdm-field">
        <span className="bdm-label">{label}</span>
        <span className={`bdm-value${currency ? ' bdm-value--currency' : ''}${muted ? ' bdm-value--muted' : ''}`}>
            {currency ? formatCurrency(value) : (value || '—')}
        </span>
    </div>
);

const BudgetDetailModal = ({ budget, visible, onHide, onDescargar, onRecotizar, descargando = false }) => {
    const isMobile = useIsMobile();

    if (!budget) return null;

    const vehicle = budget.vehicle;
    const advisor = budget.advisor;
    // Lo arma el backend (ResumenAdminBudget): plan, placa, residual, seguro, tasa, recotizaciones.
    const r = budget.resumen_admin || {};
    const seguro = r.seguro;

    const vehicleName = vehicle
        ? `${vehicle.brand || ''} ${vehicle.model || ''} ${vehicle.year || ''}`.trim()
        : null;

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            draggable={false}
            resizable={false}
            dismissableMask
            closeOnEscape
            style={budgetDetailDialogStyle(isMobile)}
            contentStyle={{
                padding: '20px',
                background: '#fefefe',
                borderRadius: isMobile ? '0.8rem 0.8rem 0 0' : '0.8rem',
            }}
            showHeader={false}
        >
            <div css={budgetDetailModalCSS()} className="fk-root">
                {/* ── Header ── */}
                <header className="bdm-header">
                    <div className="bdm-avatar">{initialsOf(budget.name)}</div>
                    <div className="bdm-identity">
                        <h2 className="bdm-name">{budget.name || 'Sin nombre'}</h2>
                        <p className="bdm-email">{budget.email || 'Sin email'}</p>
                        <div className="bdm-chips">
                            {budget.source && (
                                <span css={sourceChipCSS(budget.source)}>
                                    {budget.source === 'pei' ? 'Pei' : budget.source === 'trim' ? 'Trim' : budget.source === 'cliente' ? 'Cliente' : budget.source}
                                </span>
                            )}
                            {r.plan && <span css={resumenChipCSS(r.plan)}>{PLANES[r.plan] || r.plan}</span>}
                            {r.placa && (
                                <span css={resumenChipCSS('placa')}>
                                    <i className="pi pi-map-marker" /> {labelPlaca({ state: r.placa })}
                                </span>
                            )}
                            {seguro?.manual && <span css={resumenChipCSS('manual')}>Seguro manual</span>}
                            {/* status 0 es falsy: con `&&` React pintaba un "0" suelto */}
                            {budget.status ? (
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    padding: '3px 10px',
                                    borderRadius: '999px',
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    textTransform: 'capitalize',
                                }}>
                                    {budget.status}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onHide}
                        aria-label="Cerrar"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            fontSize: '1.05rem',
                            padding: '0.3rem 0.5rem',
                        }}
                    >
                        <i className="pi pi-times" />
                    </button>
                </header>

                {/* ── Vehículo ── */}
                {vehicle && (
                    <div className="bdm-vehicle-card">
                        <div className="bdm-vehicle-icon">
                            <i className="pi pi-car" />
                        </div>
                        <div className="bdm-vehicle-info">
                            <div className="bdm-vehicle-name">{vehicleName || 'Vehículo'}</div>
                            <div className="bdm-vehicle-desc">
                                {vehicle.description || '—'}
                                {vehicle.type ? ` · ${vehicle.type}` : ''}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Datos del cliente ── */}
                <div className="bdm-section">
                    <h3 className="bdm-section-title">
                        <i className="pi pi-user" /> Datos del cliente
                    </h3>
                    <div className="bdm-grid">
                        <Field label="Nombre" value={budget.name} />
                        <Field label="Email" value={budget.email} />
                        <Field label="Teléfono" value={budget.phone} />
                        <Field label="Fecha de creación" value={formatDate(budget.created_at)} />
                    </div>
                </div>

                {/* ── Prompt IA — solo si vino del modo IA de la landing ── */}
                {budget.ai_prompt && (
                    <div className="bdm-ai-prompt">
                        <span className="bdm-ai-prompt-icon">✨</span>
                        <div className="bdm-ai-prompt-body">
                            <div className="bdm-ai-prompt-label">Prompt de IA</div>
                            <div className="bdm-ai-prompt-text">"{budget.ai_prompt}"</div>
                        </div>
                    </div>
                )}

                <div className="bdm-divider" />

                {/* ── Detalles financieros ── */}
                <div className="bdm-section">
                    <h3 className="bdm-section-title">
                        <i className="pi pi-dollar" /> Detalles financieros
                    </h3>
                    <div className="bdm-grid">
                        <Field label="Precio del vehículo" value={budget.price} currency />
                        <Field label="Renta mensual (con IVA)" value={budget.renta_mensual_total ?? budget.monthly_rent} currency />
                        <Field label="Enganche" value={budget.down_payment} currency />
                        <Field label="Pago inicial (con IVA)" value={budget.pago_inicial_total ?? budget.initial_payment} currency />
                        <Field label="Accesorios" value={budget.accesories} currency />
                        <Field label="Plazo" value={budget.term ? `${budget.term} meses` : null} />
                        {r.plan === 'puro' && (
                            <Field
                                label="Valor residual"
                                value={r.residual ? `${formatPct(r.residual.porcentaje)} · ${formatCurrency(r.residual.monto)}` : null}
                            />
                        )}
                        <Field label="Aseguradora" value={seguro ? `${seguro.aseguradora}${seguro.manual ? ' (manual)' : ''}` : null} />
                        <Field
                            label={seguro?.manual ? 'Folio del seguro' : 'ID del seguro'}
                            value={seguro ? `${seguro.id}${seguro.folio_auto ? ' (generado)' : ''}` : null}
                        />
                        <Field
                            label="Tasa · comisión"
                            value={r.tasa != null ? `${formatPct(r.tasa)} · ${r.comision != null ? formatPct(r.comision) : '—'}` : null}
                        />
                    </div>
                </div>

                <div className="bdm-divider" />

                {/* ── Información adicional ── */}
                <div className="bdm-section">
                    <h3 className="bdm-section-title">
                        <i className="pi pi-info-circle" /> Información adicional
                    </h3>
                    <div className="bdm-grid">
                        <Field label="Fuente" value={budget.source ? (budget.source === 'pei' ? 'Pei' : budget.source === 'trim' ? 'Trim' : budget.source === 'cliente' ? 'Cliente' : budget.source) : null} />
                        <Field label="Tipo de vehículo" value={TIPOS_VEHICULO[budget.vehicule_type] || budget.vehicule_type} />
                        <Field label="Motor" value={MOTORES[budget.engine_type_id]} />
                        <Field
                            label="Asesor"
                            value={advisor ? advisor.name : null}
                            muted={!advisor}
                        />
                        <Field label="ID del budget" value={`#${budget.id}`} />
                        <Field label="Última cotización" value={r.cotizado_at ? formatDate(r.cotizado_at) : null} />
                        <Field
                            label="Recotizaciones"
                            value={r.recotizaciones ? `${r.recotizaciones} · última ${formatDate(r.ultima_recotizacion)}` : 'Ninguna'}
                            muted={!r.recotizaciones}
                        />
                    </div>
                </div>

                {/* ── Acciones ── */}
                {(onDescargar || onRecotizar) && (
                    <footer className="bdm-footer">
                        {onDescargar && (
                            <AppButton
                                label="Descargar PDF"
                                icon="pi pi-file-pdf"
                                variant="secondary"
                                loading={descargando}
                                onClick={onDescargar}
                            />
                        )}
                        {onRecotizar && (
                            <AppButton
                                label="Recotizar"
                                icon="pi pi-calculator"
                                variant="primary"
                                onClick={onRecotizar}
                            />
                        )}
                    </footer>
                )}
            </div>
        </Dialog>
    );
};

export default BudgetDetailModal;
